/**
 * Alert Component
 * Based on shadcn/ui + Figma design specification
 *
 * @description shadcn/ui 기반 컴포넌트. CSS 변수를 활용한 테마 시스템 적용.
 *
 * Variants: Neutral, Error, Success, Warning
 * Options: Icon, Description, Action button
 */

import { ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../lib/utils';

export type AlertVariant = 'neutral' | 'error' | 'success' | 'warning';

export interface AlertProps {
  variant?: AlertVariant;
  title: string;
  description?: string;
  showIcon?: boolean;
  flipIcon?: boolean;
  action?: ReactNode;
  className?: string;
}

const alertVariants = cva(
  'relative flex items-start gap-3 p-4 rounded-lg border',
  {
    variants: {
      variant: {
        neutral: 'bg-muted/50 border-border text-foreground',
        error: 'bg-destructive/10 border-destructive/30 text-destructive',
        success: 'bg-safe/10 border-safe/30 text-safe',
        warning: 'bg-warning/10 border-warning/30 text-warning',
      },
    },
    defaultVariants: {
      variant: 'neutral',
    },
  }
);

const iconVariants = cva('w-5 h-5 flex-shrink-0', {
  variants: {
    variant: {
      neutral: 'text-muted-foreground',
      error: 'text-destructive',
      success: 'text-safe',
      warning: 'text-warning',
    },
  },
  defaultVariants: {
    variant: 'neutral',
  },
});

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const AlertCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const WarningIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const getIcon = (variant: AlertVariant) => {
  switch (variant) {
    case 'success':
      return <CheckIcon />;
    case 'error':
      return <AlertCircleIcon />;
    case 'warning':
      return <WarningIcon />;
    default:
      return <InfoIcon />;
  }
};

export const Alert: React.FC<AlertProps> = ({
  variant = 'neutral',
  title,
  description,
  showIcon = true,
  flipIcon = false,
  action,
  className,
}) => {
  return (
    <div className={cn(alertVariants({ variant }), className)}>
      {showIcon && !flipIcon && (
        <span className={iconVariants({ variant })}>
          {getIcon(variant)}
        </span>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{title}</p>
        {description && (
          <p className="mt-1 text-sm opacity-80">{description}</p>
        )}
      </div>
      {showIcon && flipIcon && (
        <span className={iconVariants({ variant })}>
          {getIcon(variant)}
        </span>
      )}
      {action && (
        <div className="flex-shrink-0">{action}</div>
      )}
    </div>
  );
};

Alert.displayName = 'Alert';
